import type { WebhookEventType } from "./webhook-events";

export const T = {
  en_GB: {
    editWebhook: "Edit Webhook",
    addNewWebhook: "Add New Webhook",
    editDescription: "Update the webhook configuration below.",
    addDescription: "Configure a webhook to receive real-time event notifications.",
    webhookCreatedTitle: "Webhook Created Successfully!",
    webhookCreatedDescription:
      "Your webhook has been created. Please copy the signing secret below - you won't be able to see it again.",
    secretCopied: "Copied",
    copy: "Copy",
    secretHint: "Use this secret to verify webhook signatures in your application.",
    name: "Name",
    namePlaceholder: "Production CRM Integration",
    endpointUrl: "Endpoint URL",
    urlPlaceholder: "https://api.example.com/webhooks/qp",
    urlHint: "The HTTPS endpoint where webhook events will be sent",
    description: "Description",
    descriptionPlaceholder: "Optional description of what this webhook is used for",
    events: "Events",
    eventsHint: "Select which events should trigger this webhook",
    active: "Active",
    selectAtLeastOneEvent: "Please select at least one event",
    somethingWentWrong: "Something went wrong",
    failedToSaveWebhook: "Failed to save webhook",
    cancel: "Cancel",
    save: "Save",
    addWebhook: "Add Webhook",
    done: "Done",
    copySecretTitle: "Copy Secret",
    copySecretDescription: "Have you copied the webhook secret? You won't be able to see it again!",
    yesCopied: "Yes, I've Copied It",
    notYet: "Not Yet",
  },
  nb_NO: {
    editWebhook: "Rediger webhook",
    addNewWebhook: "Legg til ny webhook",
    editDescription: "Oppdater webhook-konfigurasjonen nedenfor.",
    addDescription: "Konfigurer en webhook for å motta sanntids hendelsesvarslinger.",
    webhookCreatedTitle: "Webhook opprettet!",
    webhookCreatedDescription:
      "Webhooken din er opprettet. Kopier signeringshemmeligheten nedenfor - du vil ikke kunne se den igjen.",
    secretCopied: "Kopiert",
    copy: "Kopier",
    secretHint: "Bruk denne hemmeligheten til å verifisere webhook-signaturer i applikasjonen din.",
    name: "Navn",
    namePlaceholder: "Produksjons-CRM-integrasjon",
    endpointUrl: "Endepunkt-URL",
    urlPlaceholder: "https://api.example.com/webhooks/qp",
    urlHint: "HTTPS-endepunktet der webhook-hendelser sendes",
    description: "Beskrivelse",
    descriptionPlaceholder: "Valgfri beskrivelse av hva denne webhooken brukes til",
    events: "Hendelser",
    eventsHint: "Velg hvilke hendelser som skal utløse denne webhooken",
    active: "Aktiv",
    selectAtLeastOneEvent: "Velg minst én hendelse",
    somethingWentWrong: "Noe gikk galt",
    failedToSaveWebhook: "Kunne ikke lagre webhook",
    cancel: "Avbryt",
    save: "Lagre",
    addWebhook: "Legg til webhook",
    done: "Ferdig",
    copySecretTitle: "Kopier hemmelighet",
    copySecretDescription: "Har du kopiert webhook-hemmeligheten? Du vil ikke kunne se den igjen!",
    yesCopied: "Ja, jeg har kopiert den",
    notYet: "Ikke ennå",
  },
} as const;

/** Label and description of each webhook event. */
export const EVENTS = {
  en_GB: {
    "flow_instance.created": {
      label: "Flow Instance Created",
      description: "Triggered when a flow instance is created",
    },
    "flow_instance.submitted": {
      label: "Flow Instance Submitted",
      description: "Triggered when a flow instance is submitted (fires again on re-submission)",
    },
    "flow_instance.unlocked": {
      label: "Flow Instance Unlocked",
      description:
        "Triggered when a flow instance is unlocked or returned to draft from a later process step (the payload carries previousStatus and reason)",
    },
    "flow_instance.cancelled": {
      label: "Flow Instance Cancelled",
      description: "Triggered when an advice is cancelled (e.g. suitability failure)",
    },
    "flow_instance.reopened": {
      label: "Flow Instance Reopened",
      description: "Triggered when a cancelled flow instance is reopened for editing",
    },
    "flow_instance.completed": {
      label: "Flow Instance Completed",
      description: "Triggered when every process step is done and the instance is final",
    },
    "flow_instance.step_changed": {
      label: "Process Step Changed",
      description: "Triggered on every process step transition, forwards and backwards",
    },
    "client.created": {
      label: "Client Created",
      description: "Triggered when a new client is created",
    },
    "client.updated": {
      label: "Client Updated",
      description: "Triggered when a client is updated",
    },
    "client.deleted": {
      label: "Client Deleted",
      description: "Triggered when a client is deleted",
    },
    "compliance.requested": {
      label: "Compliance Approval Requested",
      description: "Triggered when a flow instance enters the compliance review step",
    },
    "compliance.approved": {
      label: "Compliance Approved",
      description: "Triggered when a compliance user approves a flow instance",
    },
    "compliance.rejected": {
      label: "Compliance Rejected",
      description: "Triggered when a compliance user rejects a flow instance with a comment",
    },
    "compliance.withdrawn": {
      label: "Compliance Request Withdrawn",
      description: "Triggered when the advisor withdraws a pending compliance request",
    },
    "external_approval.requested": {
      label: "External Approval Requested",
      description: "Triggered when a flow instance enters the external review step",
    },
    "external_approval.approved": {
      label: "External Approval Granted",
      description: "Triggered when an external system approves a flow instance via the API",
    },
    "external_approval.rejected": {
      label: "External Approval Rejected",
      description: "Triggered when an external system rejects a flow instance via the API",
    },
    "external_approval.withdrawn": {
      label: "External Approval Withdrawn",
      description: "Triggered when the advisor withdraws a pending external approval request",
    },
    "signature_order.created": {
      label: "Signature Order Created",
      description: "Triggered when a signature request is created (eID or simple signature)",
    },
    "signature_order.cancelled": {
      label: "Signature Order Cancelled",
      description: "Triggered when a signature request is cancelled or deleted",
    },
    "signature_order.signed": {
      label: "Signature Order Signed",
      description: "Triggered when a document is signed (eID or simple signature)",
    },
    "pdf.generated": {
      label: "PDF Generated",
      description: "Triggered when a PDF report is generated",
    },
    "pdf.deleted": {
      label: "PDF Deleted",
      description: "Triggered when a generated PDF is deleted",
    },
    "flow.created": {
      label: "Flow Created",
      description: "Triggered when a flow is created, including from a template or as a copy",
    },
    "flow.updated": {
      label: "Flow Updated",
      description:
        "Triggered when a flow or anything in it changes (settings, pages, fields, components), including every status change (inactive, preview, active). Sent at most once every 5 minutes per flow, up to 5 minutes after the change",
    },
    "flow.activated": {
      label: "Flow Activated",
      description: "Triggered when a flow's status is set to active (from inactive or preview)",
    },
    "flow.deactivated": {
      label: "Flow Deactivated",
      description: "Triggered when an active flow's status is changed to inactive or preview",
    },
    "flow.deleted": {
      label: "Flow Deleted",
      description: "Triggered when a flow is deleted",
    },
  } satisfies Record<WebhookEventType, { label: string; description: string }>,
  nb_NO: {
    "flow_instance.created": {
      label: "Flytinstans opprettet",
      description: "Utløses når en flytinstans opprettes",
    },
    "flow_instance.submitted": {
      label: "Flytinstans sendt inn",
      description: "Utløses når en flytinstans sendes inn (utløses igjen ved ny innsending)",
    },
    "flow_instance.unlocked": {
      label: "Flytinstans låst opp",
      description:
        "Utløses når en flytinstans låses opp eller sendes tilbake til utkast fra et senere prosesstrinn (nyttelasten har previousStatus og reason)",
    },
    "flow_instance.cancelled": {
      label: "Flytinstans kansellert",
      description: "Utløses når et råd kanselleres (f.eks. egnethetssvikt)",
    },
    "flow_instance.reopened": {
      label: "Flytinstans gjenåpnet",
      description: "Utløses når en kansellert flytinstans gjenåpnes for redigering",
    },
    "flow_instance.completed": {
      label: "Flytinstans fullført",
      description: "Utløses når alle prosesstrinn er fullført og instansen er endelig",
    },
    "flow_instance.step_changed": {
      label: "Prosesstrinn endret",
      description: "Utløses ved hver trinnovergang i prosessen, framover og bakover",
    },
    "client.created": {
      label: "Kunde opprettet",
      description: "Utløses når en ny kunde opprettes",
    },
    "client.updated": {
      label: "Kunde oppdatert",
      description: "Utløses når en kunde oppdateres",
    },
    "client.deleted": {
      label: "Kunde slettet",
      description: "Utløses når en kunde slettes",
    },
    "compliance.requested": {
      label: "Compliance-godkjenning forespurt",
      description: "Utløses når en flytinstans går inn i compliance-vurderingstrinnet",
    },
    "compliance.approved": {
      label: "Compliance godkjent",
      description: "Utløses når en compliance-bruker godkjenner en flytinstans",
    },
    "compliance.rejected": {
      label: "Compliance avvist",
      description: "Utløses når en compliance-bruker avviser en flytinstans med kommentar",
    },
    "compliance.withdrawn": {
      label: "Compliance-forespørsel trukket",
      description: "Utløses når rådgiveren trekker en ventende compliance-forespørsel",
    },
    "external_approval.requested": {
      label: "Ekstern godkjenning forespurt",
      description: "Utløses når en flytinstans går inn i trinnet for ekstern vurdering",
    },
    "external_approval.approved": {
      label: "Ekstern godkjenning gitt",
      description: "Utløses når et eksternt system godkjenner en flytinstans via API-et",
    },
    "external_approval.rejected": {
      label: "Ekstern godkjenning avvist",
      description: "Utløses når et eksternt system avviser en flytinstans via API-et",
    },
    "external_approval.withdrawn": {
      label: "Ekstern godkjenning trukket",
      description: "Utløses når rådgiveren trekker en ventende ekstern godkjenningsforespørsel",
    },
    "signature_order.created": {
      label: "Signeringsordre opprettet",
      description: "Utløses når en signeringsforespørsel opprettes (eID eller enkel signatur)",
    },
    "signature_order.cancelled": {
      label: "Signeringsordre kansellert",
      description: "Utløses når en signeringsforespørsel kanselleres eller slettes",
    },
    "signature_order.signed": {
      label: "Signeringsordre signert",
      description: "Utløses når et dokument signeres (eID eller enkel signatur)",
    },
    "pdf.generated": {
      label: "PDF generert",
      description: "Utløses når en PDF-rapport genereres",
    },
    "pdf.deleted": {
      label: "PDF slettet",
      description: "Utløses når en generert PDF slettes",
    },
    "flow.created": {
      label: "Flyt opprettet",
      description: "Utløses når en flyt opprettes, også fra mal eller som kopi",
    },
    "flow.updated": {
      label: "Flyt oppdatert",
      description:
        "Utløses når en flyt eller noe i den endres (innstillinger, sider, felter, komponenter), også ved hver statusendring (inaktiv, forhåndsvisning, aktiv). Sendes maks én gang hvert 5. minutt per flyt, opptil 5 minutter etter endringen",
    },
    "flow.activated": {
      label: "Flyt aktivert",
      description:
        "Utløses når en flyts status settes til aktiv (fra inaktiv eller forhåndsvisning)",
    },
    "flow.deactivated": {
      label: "Flyt deaktivert",
      description: "Utløses når en aktiv flyts status endres til inaktiv eller forhåndsvisning",
    },
    "flow.deleted": {
      label: "Flyt slettet",
      description: "Utløses når en flyt slettes",
    },
  } satisfies Record<WebhookEventType, { label: string; description: string }>,
} as const;
